import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader,
  X,
  XCircle,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { PacemakerIcon } from "@/components/icons/PacemakerIcon";
import { PersonHeartIcon } from "@/components/icons/PersonHeartIcon";
import { TubeIcon } from "@/components/icons/TubeIcon";
import { TimeIcon } from "@/components/icons/TimeIcon";
import { RadiationIcon } from "@/components/icons/RadiationIcon";
import { ContrastIcon } from "@/components/icons/ContrastIcon";
import { PrepIcon } from "@/components/icons/PrepIcon";
import { UserIcon } from "@/components/icons/UserIcon";
import { typedSupabase } from "@/integrations/supabase/database-extensions";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { trackEvent } from "@/utils/analytics";

import { IOSBottomSheet } from "./IOSBottomSheet";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { languageMap } from "@/i18n";
import { resolveProductKey } from "@/locales";
import { formatPrice, Product } from "../types";
import { CurrencyAmount } from "@/components/CurrencyAmount";
import { BookingLanguage } from "@/types/booking";
import { CheckDisc } from "../MembershipBenefits";
import { InfoIcon } from "@/components/icons/InfoIcon";
import { useMembership } from "@/contexts/MembershipContext";
import { useMarket } from "@/hooks/useMarket";
import {
  isBloodTestProduct,
  isImagingInfoRow,
} from "@/utils/isBloodTestProduct";

// Icon mapping for tile and info icons
const iconMap: Record<string, React.ElementType> = {
  pacemaker: PacemakerIcon,
  person_heart: PersonHeartIcon,
  tube: TubeIcon,
  time: TimeIcon,
  radiation: RadiationIcon,
  contrast: ContrastIcon,
  prep: PrepIcon,
  user: UserIcon,
};

interface Tile {
  titleKey: string;
  descriptionKey: string;
  icon: string;
}

interface WhatYouGetItem {
  labelKey: string;
}

interface InfoRow {
  labelKey: string;
  valueKey: string;
  icon: string;
}

interface BiomarkerCategory {
  nameKey: string;
  items: string[];
}

interface BiomarkersData {
  titleKey: string;
  categories: BiomarkerCategory[];
}

interface GpConfirmation {
  titleKey: string;
  descriptionKey: string;
}

interface DialogContent_USP {
  tiles: Tile[];
  whatYouGet: WhatYouGetItem[];
  info: InfoRow[];
  biomarkers?: BiomarkersData;
  gpConfirmation?: GpConfirmation;
  descriptionKey?: string;
}

interface ProductUspsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  productId: string;
  productName: string;
  language: BookingLanguage;
  onSelectAndContinue?: () => void;
  useCase?: "package_dialog_content" | "addon_dialog_content";
}

export const ProductUspsModal: React.FC<ProductUspsModalProps> = ({
  isOpen,
  onClose,
  product,
  productId,
  productName,
  language,
  onSelectAndContinue,
  useCase = "package_dialog_content",
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { isMember, getEffectivePrice } = useMembership();
  const [uspData, setUspData] = useState<DialogContent_USP | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biomarkersExpanded, setBiomarkersExpanded] = useState(false);
  const hasTrackedOpen = useRef(false);
  const isMobile = useBreakpoint(640);

  // Track dialog open
  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackEvent({
        event: "package_dialog_view",
        props: { product_id: productId, product_name: productName },
      });
    }
    if (!isOpen) {
      hasTrackedOpen.current = false;
    }
  }, [isOpen, productId, productName]);

  useEffect(() => {
    if (!isOpen) {
      setReady(false);
      return;
    }
    const fetchUsps = async () => {
      setLoading(true);
      setReady(false);
      setError(null);
      setUspData(null);
      setBiomarkersExpanded(false);

      const minDelay = new Promise((r) => setTimeout(r, 600));

      try {
        const [, result] = await Promise.all([
          minDelay,
          typedSupabase
            .from("usp")
            .select("usp")
            .eq("product_id", productId)
            .eq("use_case", useCase)
            .maybeSingle(),
        ]);

        if (result.error) throw result.error;
        if (result.data && result.data.usp)
          setUspData(result.data.usp as DialogContent_USP);
      } catch (err) {
        console.error("Error fetching USP data:", err);
        setError(t("productUsps.loadError"));
      } finally {
        setLoading(false);
        // Small delay then fade in
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setReady(true));
        });
      }
    };
    fetchUsps();
  }, [isOpen, productId, language, useCase]);

  const resolve = (key: string | null | undefined): string => {
    return resolveProductKey(language, key);
  };

  // DE market has no imaging, so any what-you-get line that mentions MRI/MRT
  // (e.g. "…or MRI imaging anytime" / "…MRT-Untersuchungen…") is wrong there —
  // match on the resolved text (not the CMS labelKey index) so it's caught in
  // both languages, and show a generic line instead.
  const { market } = useMarket();
  const resolveWhatYouGet = (labelKey: string): string => {
    const resolved = resolve(labelKey);
    if (market === "de" && /\bMR[IT]\b/i.test(resolved)) {
      return resolve("addonDialog.moreAddonsDe");
    }
    return resolved;
  };

  // Radiation and contrast agent only apply to imaging (AHD-13466).
  const isBloodTest = isBloodTestProduct(product);

  // Build metadata chips: "Duration: X min · No radiation · No contrast dye"
  const buildMetadataChips = (): string[] => {
    const chips: string[] = [];
    if (product.duration_minutes) {
      chips.push(
        `${t("packageDialog.info.timeNeeded")}: ${product.duration_minutes} min`,
      );
    }
    if (isBloodTest) return chips;
    if (product.radiation_details) {
      const radiationText = resolve(product.radiation_details as string);
      if (radiationText) chips.push(radiationText);
    } else if (product.contrast_dye === false) {
      chips.push(t("usp.noRadiation"));
    }
    if (product.contrast_dye === false) {
      chips.push(t("usp.noContrast"));
    }
    return chips;
  };

  const handleSelectAndContinue = () => {
    trackEvent({
      event: "package_dialog_select",
      props: { product_id: productId, product_name: productName },
    });
    onSelectAndContinue?.();
  };

  const renderIcon = (
    iconName: string,
    className = "w-6 h-6 text-[#2d2d2d] flex-shrink-0",
  ) => {
    const IconComponent = iconMap[iconName];
    if (!IconComponent) return null;
    return <IconComponent className={className} />;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col justify-center items-center text-gray-500 min-h-[40vh]">
          <Loader className="w-8 h-8 animate-spin" />
          <p className="mt-4 text-sm">{t("common.loadingDetails")}</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex flex-col justify-center items-center h-full text-red-500 text-center px-6 min-h-[200px]">
          <XCircle className="w-8 h-8" />
          <p className="mt-4 text-sm font-semibold">{error}</p>
        </div>
      );
    }

    const description = uspData?.descriptionKey
      ? resolve(uspData.descriptionKey)
      : product.translations?.description || resolve(product.description);
    // Same data-driven qualifier pill as the add-on card (e.g. "Men 40+").
    const qualifierText = product.qualifier_tag_key
      ? t(product.qualifier_tag_key)
      : "";
    const metadataChips = buildMetadataChips();
    const infoRows = (uspData?.info ?? []).filter(
      (row) => !isBloodTest || !isImagingInfoRow(row),
    );
    const originalAmount = product.default_price?.unit_amount || 0;
    const memberAmount = isMember ? getEffectivePrice(product) : originalAmount;
    const hasDiscount = isMember && memberAmount < originalAmount;
    const price = product.default_price
      ? formatPrice(product.default_price.unit_amount, market)
      : null;

    return (
      <div className="flex flex-col gap-4 sm:gap-6 px-4 sm:px-6">
        {/* Header: Icon + Name + Metadata + Price */}
        <div className="flex gap-4 items-start">
          {product.images?.[0] && (
            <div className="w-[92px] h-[92px] rounded-2xl bg-[#f6f6f6] flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src={product.images[0]}
                alt={productName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          )}
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <h3 className="text-[22px] sm:text-[28px] font-medium leading-7 sm:leading-8 text-[#1f1f1f]">
              {productName}
              {qualifierText && (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs leading-tight text-neutral-600 align-middle normal-case">
                  {qualifierText}
                </span>
              )}
            </h3>
            {metadataChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 text-[#777679] text-base">
                {metadataChips.map((chip, i) => (
                  <div key={i} className="contents">
                    {i > 0 && <span className="text-xs">·</span>}
                    <span>{chip}</span>
                  </div>
                ))}
              </div>
            )}
            {price && (
              <div className="text-[22px] leading-[130%] text-[#2d2d2d]">
                <CurrencyAmount
                  market={market}
                  symbolClassName="text-[14px] font-medium tracking-[0.1px]"
                >
                  {hasDiscount && (
                    <span className="text-[#757575] line-through">
                      {formatPrice(originalAmount, market, true)}
                    </span>
                  )}{" "}
                  <span className="font-bold">
                    {formatPrice(
                      hasDiscount ? memberAmount : originalAmount,
                      market,
                      true,
                    )}
                  </span>
                </CurrencyAmount>
              </div>
            )}
          </div>
        </div>

        {/* GP Confirmation Banner */}
        {product.warning && (
          <div className="bg-[#f1f5fd] rounded-2xl p-4 flex gap-4 items-start overflow-hidden">
            <InfoIcon className="flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-[#2d2d2d] leading-[1.4] py-1">
              {resolve(product.warning)}
            </p>
          </div>
        )}

        {/* Description */}
        {description && (
          <div className="px-4">
            <p className="text-base leading-[1.4] text-black">{description}</p>
          </div>
        )}

        {/* Info Tiles - 2 column */}
        {uspData?.tiles && uspData.tiles.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2">
            {uspData.tiles.map((tile, i) => (
              <div
                key={i}
                className="bg-[#f6f6f6] rounded-2xl p-4 flex-1 flex gap-4 items-start min-w-0"
              >
                <div className="flex-shrink-0">{renderIcon(tile.icon)}</div>
                <div className="flex flex-col gap-1 py-1 min-w-0">
                  <p className="text-base font-medium text-black leading-[1.2]">
                    {resolve(tile.titleKey)}
                  </p>
                  {(() => {
                    const text = resolve(tile.descriptionKey);
                    const sentences = text
                      .split(/\.\s+/)
                      .filter((s) => s.length > 0);
                    if (sentences.length > 1) {
                      return (
                        <ul className="text-sm text-black leading-[1.4] list-disc pl-4 flex flex-col gap-0.5">
                          {sentences.map((s, j) => (
                            <li key={j}>{s.replace(/\.$/, "")}</li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p className="text-sm text-black leading-[1.4]">{text}</p>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* What you will get */}
        {uspData?.whatYouGet && uspData.whatYouGet.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="px-4 py-2">
              <p className="text-base font-medium text-black">
                {resolve("packageDialog.whatYouGet")}
              </p>
            </div>
            <div className="flex flex-col">
              <div className="h-px bg-gray-200" />
              {uspData.whatYouGet.map((item, i) => (
                <div
                  key={i}
                  className="min-h-[44px] flex items-center gap-2 px-4 py-1"
                >
                  <CheckDisc />
                  <span className="text-base text-[#2d2d2d]">
                    {resolveWhatYouGet(item.labelKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Biomarkers */}
        {uspData?.biomarkers && uspData.biomarkers.categories.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="px-4 py-2">
              <p className="text-base font-medium text-black">
                {resolve(uspData.biomarkers.titleKey)}
              </p>
            </div>
            {(() => {
              const allItems: {
                categoryName: string;
                categoryKey: string;
                itemKey: string;
              }[] = [];
              for (const cat of uspData.biomarkers!.categories) {
                for (const item of cat.items) {
                  allItems.push({
                    categoryName: resolve(cat.nameKey),
                    categoryKey: cat.nameKey,
                    itemKey: item,
                  });
                }
              }
              const INITIAL_COUNT = 10;
              const visibleItems = biomarkersExpanded
                ? allItems
                : allItems.slice(0, INITIAL_COUNT);
              const hasMore = allItems.length > INITIAL_COUNT;

              // Build rendered elements (headers + items)
              const elements: React.ReactNode[] = [];
              let lastCategory = "";
              visibleItems.forEach((entry, i) => {
                if (entry.categoryName !== lastCategory) {
                  if (lastCategory !== "") {
                    elements.push(
                      <div key={`sep-${i}`} className="sm:col-span-2 px-4 py-1">
                        <div className="h-px bg-[#e0e0e0]" />
                      </div>,
                    );
                  }
                  lastCategory = entry.categoryName;
                  elements.push(
                    <div
                      key={`cat-${i}`}
                      className="px-4 pt-3 pb-1 sm:col-span-2"
                    >
                      <p className="text-[14px] font-medium leading-6 text-[#2d2d2d]">
                        {entry.categoryName}
                      </p>
                    </div>,
                  );
                }
                elements.push(
                  <div
                    key={`item-${i}`}
                    className="h-[36px] flex items-center px-4"
                  >
                    <span className="text-sm text-[#2d2d2d]">
                      {resolve(entry.itemKey)}
                    </span>
                  </div>,
                );
              });

              return (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    {elements}
                  </div>
                  {hasMore && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setBiomarkersExpanded(!biomarkersExpanded);
                      }}
                      className="flex items-center justify-center gap-1 p-1 mt-1"
                    >
                      <div className="flex-1 h-px bg-[#e0e0e0]" />
                      <span className="text-sm font-medium text-[#202020] whitespace-nowrap px-1">
                        {biomarkersExpanded
                          ? resolve("packageDialog.biomarkers.viewLess")
                          : resolve("packageDialog.biomarkers.viewMore")}
                      </span>
                      {biomarkersExpanded ? (
                        <ChevronUp className="w-5 h-5 text-[#202020]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#202020]" />
                      )}
                      <div className="flex-1 h-px bg-[#e0e0e0]" />
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Information */}
        {infoRows.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="px-4 py-2">
              <p className="text-base font-medium text-black">
                {resolve("packageDialog.information")}
              </p>
            </div>
            <div className="bg-[#f6f6f6] rounded-2xl p-4">
              <div className="flex flex-col gap-3">
                {infoRows.map((row, i) => (
                  <div key={i} className="flex items-start gap-3">
                    {renderIcon(
                      row.icon,
                      "w-6 h-6 text-[#2d2d2d] flex-shrink-0 mt-0.5",
                    )}
                    {/* Label keeps its width on the left; the value takes the
                        rest of the row (flex-1) and right-aligns, wrapping to
                        multiple lines within its own column when long — instead
                        of being squeezed into a narrow ragged block. (AHD-6742) */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-8 flex-1 min-w-0">
                      <span className="text-base font-medium sm:font-normal text-[#2d2d2d] leading-6 sm:flex-shrink-0">
                        {resolve(row.labelKey)}
                      </span>
                      <span className="text-base font-normal text-[#2d2d2d] leading-6 sm:flex-1 sm:text-right">
                        {resolve(row.valueKey)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderButtons = () => (
    <div className="flex-shrink-0 px-4 py-4 sm:p-6 flex gap-3 sm:gap-4">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="normal-case bg-[#f6f6f6] hover:bg-[#ececec] text-[#48214a] rounded-full h-[40px] sm:h-[44px] px-4 sm:px-8 font-medium text-sm sm:text-base flex-1 flex items-center justify-center transition-colors"
      >
        {resolve(
          useCase === "addon_dialog_content"
            ? "addonDialog.close"
            : "packageDialog.close",
        )}
      </button>
      {onSelectAndContinue && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSelectAndContinue();
          }}
          className="normal-case bg-[#48214a] hover:bg-[#48214a]/90 text-white rounded-full h-[40px] sm:h-[44px] px-4 sm:px-8 font-medium text-sm sm:text-base flex-1 flex items-center justify-center gap-2 transition-colors"
        >
          <Check className="w-4 h-4" />
          <span>
            {useCase === "addon_dialog_content"
              ? resolve("addonDialog.select")
              : isMobile
                ? t("packageDialog.select", { name: "" }).trim()
                : t("packageDialog.select", { name: productName })}
          </span>
        </button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <IOSBottomSheet isOpen={isOpen} onClose={onClose}>
        <div className="flex h-full flex-col relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-full z-10"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex-1 overflow-y-auto pt-16 pb-10">
            <div
              className="transition-opacity duration-500 ease-out"
              style={{ opacity: loading ? 1 : ready ? 1 : 0 }}
            >
              {renderContent()}
            </div>
          </div>
          {!loading && !error && (
            <div
              className="transition-opacity duration-500 ease-out"
              style={{ opacity: ready ? 1 : 0 }}
            >
              <div className="pointer-events-none h-8 -mt-8 relative z-10 bg-gradient-to-t from-white to-transparent" />
              <div className="flex-shrink-0 bg-white">{renderButtons()}</div>
            </div>
          )}
        </div>
      </IOSBottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal forceMount>
        {/* Scrollable overlay */}
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/80 overscroll-none"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <div className="flex min-h-full justify-center py-8 px-4">
            <div
              className="relative w-full max-w-[800px] h-fit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button outside the card */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="absolute -top-2 -right-12 hidden sm:flex w-8 h-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors z-10"
                aria-label={t("common.close")}
              >
                <X className="w-5 h-5" />
              </button>
              <div
                role="dialog"
                aria-modal="true"
                aria-label={productName || "Package details"}
                className="bg-background rounded-3xl shadow-lg w-full"
                style={{
                  maxHeight: loading ? "50vh" : undefined,
                  transition: "max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: loading ? "hidden" : undefined,
                }}
              >
                <div className={`pt-10 ${loading || error ? "pb-10" : "pb-4"}`}>
                  <div
                    className="transition-opacity duration-500 ease-out"
                    style={{ opacity: loading ? 1 : ready ? 1 : 0 }}
                  >
                    {renderContent()}
                  </div>
                </div>
                {/* Buttons inside the card */}
                {!loading && !error && (
                  <div
                    className="sticky bottom-0 z-10 transition-opacity duration-500 ease-out"
                    style={{ opacity: ready ? 1 : 0 }}
                  >
                    <div className="pointer-events-none h-8 bg-gradient-to-t from-background to-transparent" />
                    <div className="bg-background rounded-b-3xl">
                      {renderButtons()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
